
import { useTranslations } from "use-intl";
import {
  Handshake,
  LayoutDashboard,
  HelpCircle,
  BookOpen,
  FileText,
  Users,
  CreditCard,
  Wallet,
  BarChart3,
  Settings,
  Mic,
  Headphones,
  BookMarked,
  PenLine,
  Layers,
  TrendingUp,
  Megaphone,
  ClipboardList,
  History,
  Scale,
  MessageSquare,
  Cpu,
} from "lucide-react";
import { Link, usePathname } from "@/i18n/navigation";
import { LogoMark } from "@/components/shared/logo-mark";
import { cn } from "@/utils/cn";

const QUESTION_BANK_SUB_ITEMS = [
  { skill: "speaking", labelKey: "nav.mockTypes.speaking", Icon: Mic },
  { skill: "listening", labelKey: "nav.mockTypes.listening", Icon: Headphones },
  { skill: "reading", labelKey: "nav.mockTypes.reading", Icon: BookMarked },
  { skill: "writing", labelKey: "nav.mockTypes.writing", Icon: PenLine },
  { skill: "full", labelKey: "nav.mockTypes.full", Icon: Layers },
] as const;

const NAV_ITEMS = [
  { href: "/admin", labelKey: "nav.dashboard", Icon: LayoutDashboard },
  { href: "/admin/questions", labelKey: "nav.questions", Icon: HelpCircle, subItems: QUESTION_BANK_SUB_ITEMS },
  { href: "/admin/exams", labelKey: "nav.exams", Icon: FileText },
  { href: "/admin/passages", labelKey: "nav.passages", Icon: BookOpen },
  { href: "/admin/users", labelKey: "nav.users", Icon: Users },
  { href: "/admin/attempts", labelKey: "nav.attempts", Icon: History },
  { href: "/admin/subscriptions", labelKey: "nav.subscriptions", Icon: CreditCard },
  { href: "/admin/payments", labelKey: "nav.payments", Icon: Wallet },
  { href: "/admin/revenue", labelKey: "nav.revenue", Icon: TrendingUp },
  { href: "/admin/partner-report", labelKey: "nav.partnerReport", Icon: Handshake },
  { href: "/admin/ai-costs", labelKey: "nav.aiCosts", Icon: Cpu },
  { href: "/admin/analytics", labelKey: "nav.analytics", Icon: BarChart3 },
  { href: "/admin/announcements", labelKey: "nav.announcements", Icon: Megaphone },
  { href: "/admin/surveys", labelKey: "nav.surveys", Icon: ClipboardList },
  { href: "/admin/legal", labelKey: "nav.legal", Icon: Scale },
  { href: "/admin/contact", labelKey: "nav.contact", Icon: MessageSquare },
  { href: "/admin/settings", labelKey: "nav.settings", Icon: Settings },
] as const;

export function AdminSidebar() {
  const t = useTranslations("admin");
  const pathname = usePathname();

  return (
    <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-card/50 lg:flex">
      <div className="flex h-16 items-center gap-2 border-b border-border px-6">
        <LogoMark className="h-7 w-7" />
        <span className="text-sm font-semibold tracking-tight">{t("appName")}</span>
      </div>

      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
        {NAV_ITEMS.map(({ href, labelKey, Icon, ...rest }) => {
          const isActive = href === "/admin" ? pathname === href : pathname.startsWith(href);
          const subItems = "subItems" in rest ? rest.subItems : undefined;
          return (
            <div key={href} className="flex flex-col gap-0.5">
              <Link
                href={href}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {t(labelKey)}
              </Link>
              {subItems && isActive && (
                <div className="ml-4 flex flex-col gap-0.5 border-l border-border pl-3">
                  {subItems.map((sub) => (
                    <Link
                      key={sub.skill}
                      href={`/admin/questions?skill=${sub.skill}`}
                      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <sub.Icon className="h-3.5 w-3.5" aria-hidden="true" />
                      {t(sub.labelKey)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
